// ─────────────────────────────────────────────────────────────────────────────
// FONTE DATI UNICA DEI TRATTAMENTI
// Tutti i componenti (Home, Indice, Menu, Form, pagine di dettaglio) leggono da
// qui. Modificare un trattamento in un solo posto -> aggiornato ovunque.
//
// Ordine e categorie: da revisione cliente (nota vocale del 21/04).
// Testi lunghi e benefici: presi dal catalogo WhatsApp Business dello studio.
// ─────────────────────────────────────────────────────────────────────────────

// Immagini trattamenti
import shirodharaImg from "@/assets/treatments/shirodhara.webp";
import takradharaImg from "@/assets/treatments/takradhara.webp";
import ubatanImg from "@/assets/treatments/ubatan.webp";
import marmaImg from "@/assets/treatments/marma.webp";
import swedanaImg from "@/assets/treatments/swedana.webp";
import pindaswedaImg from "@/assets/treatments/pindasweda.webp";
import kalariImg from "@/assets/treatments/kalari.webp";
import sarvangaImg from "@/assets/treatments/sarvanga.webp";
import garbabhyangamImg from "@/assets/treatments/garbabhyangam.webp";
import shantalaImg from "@/assets/treatments/shantala.webp";
import mukhabhyangamImg from "@/assets/treatments/mukhabhyangam.webp";
import suonoterapiaImg from "@/assets/treatments/suonoterapia.webp";
import cristalloterapiaImg from "@/assets/treatments/cristalloterapia.webp";
import hotStoneImg from "@/assets/treatments/hot-stone.webp";
import visoImg from "@/assets/treatments/trattamenti-viso.webp";
import depilazioneImg from "@/assets/treatments/depilazione-caramello.webp";
import riflessologiaImg from "@/assets/treatments/riflessologia.webp";

export type TreatmentCategory = "ayurvedico" | "altri";

export interface Treatment {
  slug: string;
  /** Nome mostrato in menu, card e titoli */
  name: string;
  /** Categoria: trattamenti ayurvedici oppure "altri trattamenti" */
  category: TreatmentCategory;
  /** Immagine di copertina */
  image: string;
  /** Descrizione breve per le card */
  short: string;
  /** Corpo del testo (paragrafi) — dal catalogo dello studio */
  body: string[];
  /** Elenco benefici — dal catalogo dello studio */
  benefits: string[];
  /** Nota conclusiva opzionale (es. idea regalo) */
  note?: string;
}

// L'ordine dell'array è l'ordine di priorità voluto dallo studio.
export const treatments: Treatment[] = [
  // ── TRATTAMENTI AYURVEDICI (ordine di priorità 1 → 13) ────────────────────
  {
    slug: "sarvanga",
    name: "Sarvanga Abhyangam",
    category: "ayurvedico",
    image: sarvangaImg,
    short: "Massaggio tradizionale ayurvedico per il benessere totale del corpo.",
    body: [
      "Il Sarvanga Abhyangam è il massaggio ayurvedico per antonomasia, praticato da millenni in tutta l'India. In sanscrito la parola Sarvanga significa “tutto il corpo” mentre la parola Abhyangam significa “massaggio con olio”.",
    ],
    benefits: [
      "La sua pratica trasmette un senso totale di benessere, in quanto aiuta a ristabilire l'equilibrio e l'armonia tra mente corpo e spirito, che in ayurveda costituiscono un tutt'uno.",
      "L'Abhyangam fortifica le difese immunitarie andando a trattare ogni parte del corpo e disintossicando la funzionalità renale, digestiva, epatica.",
      "È un massaggio in cui vengono utilizzati oli caldi e una vasta gamma di manualità scelti in relazione alla tipologia costituzionale del soggetto. Come tutti i trattamenti ayurvedici, anche il massaggio Abhyangam è valutato in base alle reali necessità della persona.",
    ],
  },
  {
    slug: "swedana",
    name: "Swedana",
    category: "ayurvedico",
    image: swedanaImg,
    short: "Trattamento di sudorazione terapeutica per la purificazione.",
    body: [
      "Lo Swedana è un particolare bagno di vapore della tradizione ayurvedica che ha come scopo quello di eliminare dal nostro corpo tutto l'accumulo di tossine nocive. Il termine Swedana deriva dal sanscrito “swid” che significa letteralmente “sudare”.",
      "Durante il trattamento del bagno di vapore Swedana, immergiamo completamente il nostro corpo all'interno di una particolare struttura posta sul lettino, lasciando all'esterno soltanto la testa, dandoci quindi la possibilità di non surriscaldare la testa e respirare tranquillamente, rendendo il tutto più piacevole di una sauna o di un bagno turco, dove invece l'aria calda affatica il respiro e riduce il tempo di sopportazione del trattamento.",
      "Al vapore vengono talvolta aggiunti anche degli oli essenziali così da combinare gli effetti benefici dello Swedana alle proprietà benefiche dell'aromaterapia.",
      "Al termine del trattamento viene eseguito un massaggio ayurvedico così da intensificarne gli effetti benefici: in questo modo il nostro corpo risulterà pulito, purificato e rinvigorito.",
    ],
    benefits: [
      "Calma profondamente il nostro corpo e la nostra mente",
      "Allevia le tensioni muscolari e i dolori articolari",
      "Favorisce la sudorazione e l'eliminazione delle tossine",
      "Migliora la circolazione del sangue e riossigena i tessuti",
      "Sblocca i canali energetici del corpo",
      "Rende la pelle purificata, liscia, luminosa e morbida",
      "Favorisce l'eliminazione dei liquidi in eccesso, riducendo la ritenzione idrica",
    ],
  },
  {
    slug: "ubatan",
    name: "Ubatan",
    category: "ayurvedico",
    image: ubatanImg,
    short: "Trattamento esfoliante e purificante per la pelle.",
    body: [
      "Particolarmente consigliato nei cambi di stagione, in India è considerato un trattamento di bellezza, un vero e proprio rituale da eseguire agli sposi il giorno prima del matrimonio.",
      "L'Ubatan è un trattamento che si esegue attraverso un massaggio profondo, con una pasta che serve a detergere e nutrire la pelle. A questo scopo, nelle ricette dell'Ubatan si utilizza di solito un olio di sesamo, yogurt o ghee (burro chiarificato), argilla, farine di legumi – in particolare quella di ceci – e altri ingredienti scelti ad hoc per il cliente fra spezie ed erbe.",
    ],
    benefits: [
      "Riporta equilibrio ed energia a tutti gli strati cutanei",
      "Aiuta a eliminare le tossine",
      "È un peeling naturale ma anche un massaggio profondo, dalle proprietà nutritive e rinvigorenti",
      "Pulisce e nutre la pelle permettendole di respirare e lubrificarsi",
      "Dona lucentezza, stabilità e leggerezza a tutto il corpo",
      "Riduce il grasso migliorando il metabolismo del tessuto adiposo e muscolare",
      "È ideale all'inizio di una cura disintossicante",
      "Prepara la pelle per una migliore esposizione al sole durante l'estate",
    ],
  },
  {
    slug: "pindasweda",
    name: "Pindasweda",
    category: "ayurvedico",
    image: pindaswedaImg,
    short: "Massaggio con sacchetti di erbe immersi in olio caldo per il rilassamento muscolare.",
    body: [
      "Pinda = bolo (fagottino). Sweda = calore.",
      "È una terapia del calore abbinata al massaggio, praticata con dei fagottini di cotone o lino ripieni di ingredienti completamente naturali (erbe, riso, spezie, farine…) scelti in base alla costituzione della persona che si va a trattare. I fagottini vengono poi usati imbevendoli in olio caldo o latte, o addirittura a secco, in base all'effetto che si vuole ottenere.",
      "All'apparenza sembra una cosa molto semplice, ma la preparazione di questi sacchettini necessita di studio, competenza ed esperienza: conoscere le proprietà, gli effetti e le controindicazioni degli ingredienti è importantissimo. Non si può miscelare qualsiasi cosa!",
    ],
    benefits: [
      "Calore e idratazione per soggetti di tipo VATA che soffrono il freddo, i dolori articolari, la costipazione e la secchezza.",
      "Calore, depurazione e drenaggio per i soggetti KAPHA che soffrono spesso di ritenzione idrica e cellulite.",
      "Idratazione per i soggetti PITTA che però hanno bisogno di essere rinfrescati e calmati.",
    ],
  },
  {
    slug: "trattamenti-viso",
    name: "Trattamenti Viso",
    category: "ayurvedico",
    image: visoImg,
    short: "Trattamenti per il viso con l'utilizzo di erbe, frutta e spezie.",
    body: [
      "No, non è una semplice “pulizia” del viso. Si tratta semmai di un vero e proprio trattamento di nutrimento del viso… Perché ogni volto ha le proprie caratteristiche e necessità sempre diverse, dove la detersione e la pulizia sono solo il primo step di un trattamento che andrà a rendere la vostra pelle viva!",
      "Ora provate ad immaginare… Siete sdraiati comodi su un lettino, una musica dolce di sottofondo, vapore caldo che ammorbidisce la pelle del vostro viso, mentre io mi occupo di prepararvi unguenti e maschere con frutti freschi, erbe e fiori, spezie, yogurt, miele, e tutto ciò che la natura ha da offrirci “qui e ora”.",
      "Il trattamento prevede: detersione del viso con oli medicati; scrub delicato per eliminare la patina data dal tempo e dallo stress; stesura delle varie maschere personalizzate ad hoc per ottenere i migliori risultati dalla vostra pelle; massaggio ai piedi durante il tempo di stesura delle maschere; massaggio al viso finale.",
    ],
    benefits: [],
    note: "Anche questo può essere una meravigliosa idea regalo! Contattami tramite WhatsApp per altre info, o per ottenere un buono da regalare a chi vuoi bene.",
  },
  {
    slug: "mukhabhyangam",
    name: "Mukhabhyangam",
    category: "ayurvedico",
    image: mukhabhyangamImg,
    short: "Massaggio ayurvedico del viso per il ringiovanimento.",
    body: [
      "Se vi guardate allo specchio e vi accorgete di avere i lineamenti tirati, l'espressione corrucciata e il colorito spento, avete proprio bisogno di un massaggio Mukhabhyanga: il massaggio per il viso, che oltre alla pelle riporterà benessere anche al vostro spirito e alla vostra mente.",
      "L'Ayurveda, da sempre, presta un'enorme attenzione al viso in quanto in esso sono concentrati tutti e cinque gli organi di senso e un gran numero di Marma, i punti energetici attraverso i quali è possibile intervenire, tramite la loro stimolazione, sull'energia che scorre all'interno del corpo.",
    ],
    benefits: [
      "Allevia la stanchezza",
      "Contrasta le rughe d'espressione",
      "Purifica la pelle liberandola dalle impurità",
      "Nutre la pelle e ne ossigena a fondo i tessuti",
      "Nutre gli organi di senso",
      "Interviene sulla lucentezza dei vostri occhi",
      "Pacifica tutti i Dosha",
      "Riporta ad uno stato di quiete interiore",
    ],
  },
  {
    slug: "kalari",
    name: "Kalari",
    category: "ayurvedico",
    image: kalariImg,
    short: "Massaggio fortemente decontratturante eseguito dall'operatore con i piedi.",
    body: [
      "A volte, amare il proprio corpo significa “trattarlo con i piedi”.",
      "Questo succede quando hai bisogno di prendertene cura in maniera più intensa, sia per sciogliere le tensioni più profonde, sia per rafforzare, fortificare, rinvigorire i tessuti muscolari e articolari.",
      "L'operatore effettua il massaggio con i piedi, dosando il peso del proprio corpo attraverso il sostegno di una fune, effettuando movimenti di distensione e di pressione che stimolano il corpo donandogli la forza ed il vigore che veniva richiesto ai Guerrieri Kalari, antichi combattenti della regione del Kerala in India, ai quali tale trattamento era tradizionalmente rivolto.",
    ],
    benefits: [
      "Tonifica la muscolatura",
      "Scioglie le contratture",
      "Allenta la rigidità articolare",
      "Allevia la stanchezza",
    ],
  },
  {
    slug: "shirodhara",
    name: "Shirodhara",
    category: "ayurvedico",
    image: shirodharaImg,
    short: "Trattamento ayurvedico per il rilassamento profondo e il benessere mentale.",
    body: [
      "Praticato da oltre 5.000 anni, questo trattamento nasce in India e si basa sugli antichi principi dell'Ayurveda, volta ad armonizzare il corpo, la mente, i sensi e l'anima. Il nome trae sua origine dal sanscrito, “shiro” (testa) e “dhara” (flusso).",
      "Tecnicamente il trattamento consiste in una dolce colata di olio sulla fronte, con flusso regolare, lento e costante, attraverso il Dhara-Patra (recipiente utilizzato appositamente per questo trattamento) con un movimento manuale da tempia a tempia che va a stimolare sia il centro della fronte, sia il cuoio capelluto.",
      "È un trattamento che induce uno stato di rilassamento molto profondo, ed è altamente riequilibrante per l'armonia del complesso corpo-mente e le sue manifestazioni psicosomatiche.",
      "Gli oli medicati ed i decotti erbali, così come la temperatura dell'olio stesso, vengono scelti sulla base della costituzione individuale e dello squilibrio da trattare. Al posto dell'olio, specialmente nei disequilibri di Pitta, può essere utilizzato anche latte o latticello.",
    ],
    benefits: [
      "Dona profondo e immediato rilassamento",
      "Contrasta emicrania e cefalea",
      "Contrasta i disturbi d'ansia",
      "Contrasta sinusite e rinite allergica",
      "Rallenta l'incanutimento dei capelli",
      "Contrasta disturbi neurologici",
      "Contrasta l'insonnia",
      "Contrasta i problemi auditivi",
      "Contrasta alcuni tipi di malattie della pelle collegati al sistema neurologico, come ad esempio la psoriasi",
      "Nutre e rinforza tutti gli organi di senso",
    ],
  },
  {
    slug: "takradhara",
    name: "Takradhara",
    category: "ayurvedico",
    image: takradharaImg,
    short: "Variante dello Shirodhara con latticello fresco per il riequilibrio del sistema nervoso.",
    body: [
      "Il Takradhara è un altro di quei trattamenti unici appartenenti alla scienza ayurvedica. Ha un suo nome specifico, ma in realtà potremmo considerarlo una variante dello Shirodhara, in quanto si tratta dello stesso tipo di trattamento eseguito però con il latticello fresco al posto dell'olio caldo. Takra, infatti, in sanscrito significa latticello (conosciuto anche come buttermilk), e dhara significa flusso.",
      "I benefici fisiologici del trattamento Takradhara sono molto simili a quelli dello Shirodhara perché il flusso continuo sulla fronte stimola l'ipotalamo, regolando le funzioni della ghiandola pituitaria.",
      "A differenza dello Shirodhara, il Takradhara ha un effetto di raffreddamento su testa e corpo e per questo motivo, oltre ad avere grossi risultati su tutti gli squilibri tipici del dosha Vata, è particolarmente indicato per tutti quei problemi legati anche al conseguente squilibrio del dosha Pitta come, ad esempio, la psoriasi.",
    ],
    benefits: [
      "Disturbi legati al sistema nervoso centrale",
      "Stress",
      "Emicranie",
      "Insonnia",
      "Depressione",
      "Benessere di cute e capelli",
      "Eruzioni cutanee",
    ],
  },
  {
    slug: "garbabhyangam",
    name: "Garbabhyangam",
    category: "ayurvedico",
    image: garbabhyangamImg,
    short: "Massaggio ayurvedico specifico per donne in gravidanza.",
    body: [
      "Nella millenaria tradizione Ayurvedica il massaggio Garbabhyangam, ossia il massaggio ayurvedico in gravidanza, attraverso i suoi movimenti lenti e dolci, può risultare un valido rimedio per alleviare fastidi e malesseri generalmente associati al periodo della gestazione.",
      "In realtà il termine “GARBA”, in sanscrito, significa “EMBRIONE”, il che fa ben comprendere come questo tipo di massaggio sia indirizzato tanto alla futura mamma quanto al feto: la sensazione di benessere che offre arriva non solo alla madre ma anche al bimbo/a, che dal grembo assorbe ogni emozione ed esperienza.",
    ],
    benefits: [
      "Come scientificamente provato per tutti i massaggi, aumenta i livelli di serotonina e dopamina (ormoni della felicità) e diminuisce i livelli di cortisolo (indicatore di stress) migliorando in generale l'umore.",
      "Aiuta a sciogliere quelle contratture dolorose dovute al continuo adattamento posturale nella gestazione.",
      "Favorisce il drenaggio linfatico e il ritorno venoso, concedendo una piacevole sensazione di leggerezza alle gambe, prevenendo o alleviando gonfiore e ritenzione idrica.",
      "Se praticato fin dai primi mesi, è inoltre un efficace trattamento antismagliature grazie all'utilizzo di oli naturali dalle proprietà lenitive ed emollienti, in grado di mantenere la pelle morbida ed elastica.",
    ],
  },
  {
    slug: "shantala",
    name: "Shantala",
    category: "ayurvedico",
    image: shantalaImg,
    short: "Massaggio infantile della tradizione ayurvedica.",
    body: [
      "Lo Shantala rappresenta uno strumento magnifico per iniziare a comunicare con il proprio bimbo. Si tratta di un continuum energetico fra la vita uterina e quella esterna, che permette di prolungare quindi il contatto tra madre e figlio, e avviare così una relazione armoniosa tra i due.",
      "Lo Shantala, che si serve di metodologie dolci e rassicuranti, è una delle arti indiane più tradizionali tramandate dalla medicina Ayurvedica e generalmente viene eseguito dalla mamma del neonato, o tutt'al più dalla nonna.",
      "È bene che il bimbo sia a stomaco vuoto e che il massaggio venga eseguito o il mattino presto o verso l'ora del tramonto, in un luogo tranquillo, pulito e al riparo da correnti d'aria. Una volta terminato il massaggio vi è la necessità di ridare al bambino le energie attraverso il bagno.",
      "La pratica tradizionale dello Shantala dovrebbe essere realizzata sin dai primi giorni di nascita. Ma, essendo la pelle del neonato particolarmente delicata, è bene cominciare ad eseguire il massaggio vero e proprio con le mani a partire dal primo mese di vita, quando il suo corpo si sarà fortificato.",
    ],
    benefits: [
      "Agisce sul sistema circolatorio, muscolare e linfatico favorendo il ritorno del sangue verso il cuore",
      "Accresce il sistema immunitario aumentando il numero delle piastrine e dei globuli bianchi",
      "Regolarizza il sistema gastro-intestinale e favorisce la funzione sonno-veglia",
      "Idrata la pelle e tonifica i muscoli",
      "Allevia il dolore delle coliche gassose, tonificando il tratto digestivo e favorendo l'eliminazione di aria e feci",
      "Attraverso un adeguato massaggio del viso, allevia la tensione durante il periodo della dentizione e allenta la morsa del raffreddore attraverso lo scaricamento del naso",
      "Favorisce lo stato di benessere nel bambino e il rilassamento",
      "Permette il superamento di eventuali traumi infantili e stimola i sensi",
      "Favorisce il legame di attaccamento con i genitori, rafforzandolo",
    ],
    note: "Regalati o regala ad una futura mamma un corso di 1h per imparare lo Shantala.",
  },
  {
    slug: "hot-stone",
    name: "Hot Stone",
    category: "ayurvedico",
    image: hotStoneImg,
    short: "Massaggio con pietre calde di basalto per il rilassamento profondo.",
    body: [
      "La pratica del massaggio hot stone risale ad oltre 3000 anni fa. È un trattamento dove l'applicazione di specifiche pietre riscaldate viene unita al potere del massaggio. Nella tradizione ayurvedica e sciamanica americana, questa forma di termoterapia era di uso comune per donare diversi benefici.",
      "Ai giorni d'oggi vengono utilizzate principalmente delle pietre nere di basalto, derivanti dall'azione di sedimentazione della lava. Queste, oltre ad essere naturalmente molto lisce e levigate, sono ricche di ferro, magnesio e altri oligoelementi. Riescono infatti a trattenere il calore per lunghi periodi di tempo, rilasciandolo poi lentamente e in modo uniforme.",
      "Le pietre si riscaldano in appositi fornelli riempiti di acqua, portandole ad una temperatura fra i 60-70°C. Vengono poi appoggiate, sopra un apposito asciugamano, in punti specifici del corpo e strofinate poi sulla pelle con oli da massaggio che ne favoriscono lo scorrimento.",
      "Le pietre calde hanno uno straordinario potere decontratturante, disintossicante, rilassante e riequilibrante. Durante il trattamento possono essere anche alternate alle pietre fredde, mantenute tali nel ghiaccio.",
      "Le pietre fredde hanno l'enorme potere di diminuire le infiammazioni, lenire e tonificare in particolare nei casi di muscolature fortemente contratte. Inoltre il passaggio caldo/freddo permette di stimolare vaso-dilatazione e vaso-costrizione, con un effetto rigenerante del microcircolo e rivitalizzante sul sistema nervoso.",
    ],
    benefits: [],
  },
  {
    slug: "punti-marma",
    name: "Punti Marma",
    category: "ayurvedico",
    image: marmaImg,
    short: "Massaggio e digitopressione sui punti energetici per il riequilibrio.",
    body: [
      "In Ayurveda si afferma che nel corpo umano sono presenti numerosi punti di energia, in tutto 107/108, che stimolano alcune delle sue funzioni e ne mantengono la salute.",
      "Secondo i testi ayurvedici, i punti marma sono parti del corpo dove due o più sistemi importanti s'incontrano, quali i nervi o i vasi sanguigni, le ossa e i nervi o i muscoli, i legamenti e le articolazioni.",
      "I punti marma sono siti di energia concentrata, sono la Matrix Divina presente nel corpo umano. In Ayurveda sono descritte procedure, massaggi e digitopressione per armonizzare i Marma.",
    ],
    benefits: [
      "Aiuta in caso di raffreddore, difficoltà respiratoria delle prime vie aeree, mal di testa",
      "Migliora la vista, l'udito, l'olfatto e la memoria",
      "Aiuta ad alleviare il dolore, le tensioni e la rigidità di spalle, braccia e vertebre cervicali",
      "Attiva gli organi della cavità toracica e addominale, ed è consigliato in caso di stipsi, disordini mestruali e gastroenterici",
      "Sblocca eventuali tensioni neuromuscolari e ha un effetto positivo sulla colonna vertebrale",
      "Migliora la circolazione sanguigna",
      "Allontana lo stress",
      "Favorisce il sonno e il rilassamento",
    ],
  },

  // ── ALTRI TRATTAMENTI ─────────────────────────────────────────────────────
  {
    slug: "riflessologia-plantare",
    name: "Riflessologia plantare",
    category: "altri",
    image: riflessologiaImg,
    short: "Stimolazione dei punti riflessi del piede per sciogliere le tensioni e ritrovare equilibrio.",
    body: [
      "Le mani si posano sul piede come su una mappa segreta del corpo: un continente in miniatura, dove ogni collina e ogni valle racconta di un organo lontano, di un respiro, di un cuore che batte in silenzio.",
      "Il pollice preme, ruota, indugia. Non è solo pressione: è interpretazione. È il gesto antico di chi sa che il piede non è periferia, ma radice — l'ultimo punto di contatto tra noi e la terra, il primo a portare il peso di ogni giornata vissuta in piedi, in corsa, in fuga.",
      "Sotto quella pelle stanca dorme una geografia invisibile, dove ogni zona del piede fa eco a una parte di noi: una mappa tutta da ascoltare e accudire.",
      "La riflessologia moderna è stata sistematizzata in America e trova nell'antica tradizione cinese del Metodo Zu un'esatta corrispondenza. Chi pratica questa tecnica non cura il piede: si prende cura della persona intera, attraverso il piede. Scioglie tensioni che non sapevamo di avere e apre porte che il corpo aveva chiuso per proteggersi.",
    ],
    benefits: [],
  },
  {
    slug: "depilazione-caramello",
    name: "Depilazione Caramello",
    category: "altri",
    image: depilazioneImg,
    short: "Depilazione naturale con vero caramello per una pelle liscia e morbida e senza irritazioni.",
    body: [
      "È un tipo di depilazione completamente naturale di origine berbera. Si tratta di caramello vero e proprio, creato da me con ingredienti naturali quali zucchero, miele e limone, e lavorato in modo tale da creare un impasto morbido e colloso col quale avvolgere i peli ed estirparli dalla radice, senza in alcun modo irritare la pelle.",
      "Il caramello è adatto a tutti, a tutte le età, in tutte le zone del corpo e in tutte le situazioni.",
    ],
    benefits: [
      "Sia donne che uomini",
      "Sia adulti che ragazzini",
      "Viso, sopracciglia, baffi, barba uomo",
      "Zone intime (anche totali)",
      "Sia peli lunghi che molto corti",
      "No follicolite",
      "No peli incarniti",
      "Pelle morbida e liscia",
      "Completamente naturale e fatto da me!",
    ],
  },
  {
    slug: "cristalloterapia",
    name: "Cristalloterapia",
    category: "altri",
    image: cristalloterapiaImg,
    short: "Trattamento con pietre naturali per il riequilibrio energetico dei chakra.",
    body: [
      "La cristalloterapia è una disciplina olistica focalizzata sull'utilizzo di cristalli, pietre e minerali di varie forme e colori al fine di raggiungere uno stato di benessere psico-fisico stimolando le risorse energetiche naturali.",
      "La cristalloterapia si fonda sul presupposto secondo cui ogni cosa in natura possiede un peculiare campo energetico, il quale interagisce con gli eventi esterni. Noi stessi siamo fatti di energia. L'uomo è in grado di assorbire e trasformare le energie attraverso i Chakra, le porte di accesso del flusso energetico vitale del nostro corpo.",
      "I cristalli emanano energie purificatrici che vengono assorbite a livello energetico dal nostro organismo, come vibrazioni costanti. Il principio alla base della cristalloterapia è proprio la capacità delle pietre di riequilibrare e riordinare qualunque energia entri in contatto con esse.",
      "Ci sono diversi tipi di pietre e cristalli che vengono usati nella cristalloterapia. Ognuno di questi ha una valenza e un significato propri e vengono scelti in base alla persona e al momento presente che si sta affrontando. La cristalloterapia interviene a tutti e 3 i livelli: fisico, mentale, spirituale.",
    ],
    benefits: [],
  },
  {
    slug: "suonoterapia",
    name: "Suonoterapia",
    category: "altri",
    image: suonoterapiaImg,
    short: "Terapia con campane tibetane per il riequilibrio energetico.",
    body: [
      "Vengono comunemente chiamate “campane tibetane”, o “singing bowls”, ma chi ne conosce la vera storia sa che sono antichissimi strumenti rituali della Medicina Spirituale Himalayana (che col Tibet e con il buddismo ha gran poco a che fare). Attualmente i luoghi di provenienza più comuni sono Nepal e India.",
      "La loro origine risale ad oltre il 2.000 a.C. e si dice venissero forgiate con una lega di sette metalli diversi, connessi, secondo il Mandala di Guarigione, ad altrettanti sette colori, pianeti, divinità, mudra, mantra, chakra.",
      "Questa composizione di metalli crea un suono le cui vibrazioni hanno la capacità di riequilibrare l'organismo e di portare pace interiore.",
      "E la scienza conferma! Da ultimi studi è stato provato che la musica, come forma organizzata di suoni, può influenzare direttamente le onde cerebrali, l'umore, il sistema nervoso e la chimica del corpo. Di fatto può rilassare, indurre il sonno, alleviare i disturbi mentali ed i blocchi emozionali. Il suono profondo delle campane tibetane può toccare l'anima.",
    ],
    benefits: [],
  },
];

// ── Helper ──────────────────────────────────────────────────────────────────

export const getTreatmentBySlug = (slug: string): Treatment | undefined =>
  treatments.find((t) => t.slug === slug);

export const ayurvedicTreatments = treatments.filter(
  (t) => t.category === "ayurvedico",
);

export const otherTreatments = treatments.filter(
  (t) => t.category === "altri",
);
